import { useEffect, useState } from 'react';
import { adresseApi } from '../api/client';
import { piecesApi } from '../api/resources';
import { DEVISE, fmt } from '../lib/currency';
import { tr, trf } from '../lib/i18n';
import { numeroDe } from '../lib/utils';
import { piecesDesDepenses } from '../lib/pieces-depenses';
import { C } from '../theme/palette';
import { ouvrePiece } from './DocumentsJoints.jsx';
import { ouvreVisionneuse } from './Visionneuse.jsx';

/** Une pièce de coût : la facture d'un fournisseur ou d'un sous-traitant. */
export function estUnAchat(p) {
  return (
    !!String(p?.fournisseur || '').trim() ||
    !!String(p?.reference || '').trim() ||
    (p?.montant !== null && p?.montant !== undefined && p?.montant !== '')
  );
}

/** Vrai pour la facture d'un sous-traitant, rangée dans sa catégorie. */
/** Étapes des photos de chantier, dans l'ordre du chantier. */
const ETAPES = [
  ['avant', '🧱 Avant les travaux'],
  ['pendant', '🔨 Pendant'],
  ['apres', '✨ Après les travaux'],
  ['probleme', '⚠ Problème'],
];
const ETAPES_CONNUES = new Set(ETAPES.map(([cat]) => cat));

const deSousTraitance = (p) => /sous.?trait/i.test(String(p?.categorie || ''));

/** Vrai pour une image, à montrer en vignette. */
const estImage = (p) => String(p?.typeMedia || '').startsWith('image/');

/**
 * Les ids des offres et factures d'un chantier : son offre, celles qui la
 * reprennent et les factures qui le portent.
 *
 * @param {object} ch chantier
 * @param {object[]} devis offres
 * @param {object[]} factures factures clients
 * @returns {{devis: object[], factures: object[]}} ses documents
 */
export function documentsDuChantier(ch, devis, factures) {
  const d = (devis || []).filter(
    (x) => Number.isInteger(x.id) && ch.devisId != null && x.id === ch.devisId,
  );
  const f = (factures || []).filter(
    (x) =>
      Number.isInteger(x.id) &&
      ((x.siteId != null && x.siteId === ch.id) ||
        (ch.devisId != null && x.devisId === ch.devisId)),
  );
  return { devis: d, factures: f };
}

/**
 * Les factures de fournisseur et de sous-traitant jointes aux offres et
 * factures d'un chantier : sa marge les compte. L'onglet Marges et l'accueil
 * les oubliaient, et annonçaient une autre marge que la page du chantier.
 *
 * @param {object} ch chantier
 * @param {object[]} [achats] pièces chiffrées de la société ({genre, documentId, piece})
 * @param {object[]} [devis] offres
 * @param {object[]} [factures] factures
 * @returns {object[]} les pièces du chantier
 */
export function achatsDuChantier(ch, achats, devis, factures) {
  const docs = documentsDuChantier(ch, devis, factures);
  return (achats || [])
    .filter(
      (x) =>
        estUnAchat(x?.piece) &&
        (x.genre === 'DEVIS'
          ? docs.devis.some((d) => d.id === x.documentId)
          : docs.factures.some((f) => f.id === x.documentId)),
    )
    .map((x) => x.piece);
}

/**
 * Charge les pièces d'un groupe d'offres et de factures.
 *
 * @param {object[]} devis offres
 * @param {object[]} factures factures
 * @returns {object[]} leurs pièces, chacune avec son document
 */
export function usePiecesDesDocuments(devis, factures) {
  const [pieces, setPieces] = useState([]);
  const cle =
    (devis || []).map((d) => d.id).join(',') + '|' + (factures || []).map((f) => f.id).join(',');
  useEffect(() => {
    const d = (devis || []).map((x) => x.id).filter(Number.isInteger);
    const f = (factures || []).map((x) => x.id).filter(Number.isInteger);
    if (!d.length && !f.length) {
      setPieces([]);
      return undefined;
    }
    let vivant = true;
    piecesApi
      .desDocuments({ devis: d, factures: f })
      .then((liste) => vivant && setPieces(Array.isArray(liste) ? liste : []))
      .catch(() => vivant && setPieces([]));
    return () => {
      vivant = false;
    };
    // La clé résume les documents : la liste ne se recharge que s'ils changent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle]);
  return pieces;
}

/**
 * La marchandise et la sous-traitance jointes à toutes les offres et factures
 * de la société, même payées : des dépenses que l'accueil et la comptabilité
 * comptent avec les autres.
 *
 * @param {string|number} [version] change quand les pièces ont pu changer
 * @returns {object[]} les pièces chiffrées, chacune avec son document
 */
export function useAchatsJoints(version) {
  const [achats, setAchats] = useState([]);
  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(() => piecesApi.achats())
      .then((liste) => vivant && setAchats(Array.isArray(liste) ? liste : []))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [version]);
  return achats;
}

/**
 * La liste d'un client ou d'un chantier : la marchandise et la sous-traitance
 * jointes à ses offres et factures, et leurs photos. Une dépense ajoutée sur
 * une facture déjà payée y figure comme les autres.
 *
 * @param {object} props propriétés
 * @param {object[]} props.devis offres du client ou du chantier
 * @param {object[]} props.factures factures du client ou du chantier
 * @param {object[]} [props.chantiers] chantiers : ceux de ces offres et factures
 *   ajoutent leurs factures de fournisseur et de sous-traitance
 * @param {object[]} [props.pieces] pièces déjà chargées ; sinon le bloc les charge
 * @param {boolean} [props.sansPhotos] ne montrer que les achats
 * @param {(contenu: import('react').ReactElement) => import('react').ReactElement} [props.enveloppe]
 *   carte autour de la liste, posée seulement quand elle n'est pas vide
 * @returns {import('react').ReactElement|null} la liste, ou rien
 */
export function ListeDuClient({
  devis,
  factures,
  chantiers,
  pieces: chargees,
  sansPhotos = false,
  enveloppe = (contenu) => contenu,
}) {
  const lues = usePiecesDesDocuments(chargees ? [] : devis, chargees ? [] : factures);
  const surLesDocuments = chargees || lues;
  // Les factures de fournisseur et de sous-traitance saisies sur les chantiers
  // du client : ajoutées au chantier, elles n'apparaissaient pas dans le
  // classeur, à côté de la facture du client.
  const chantiersLies = (chantiers || []).filter((ch) => {
    const docs = documentsDuChantier(ch, devis, factures);
    const duClient =
      ch.clientId != null &&
      [...(devis || []), ...(factures || [])].some((d) => d.clientId === ch.clientId);
    return docs.devis.length > 0 || docs.factures.length > 0 || duClient;
  });
  // Les photos prises sur ses chantiers — avant, pendant, après les travaux —
  // rangées avec ses offres et factures dans le classeur.
  const photosChantier = sansPhotos
    ? []
    : chantiersLies.flatMap((ch) =>
        (ch.photos || []).filter((ph) => ph.src).map((ph) => ({ ...ph, chantier: ch.nom })),
      );
  const chantiersDuClient = (chantiers || []).filter((ch) => {
    const docs = documentsDuChantier(ch, devis, factures);
    const duClient =
      ch.clientId != null &&
      [...(devis || []), ...(factures || [])].some((d) => d.clientId === ch.clientId);
    return docs.devis.length > 0 || docs.factures.length > 0 || duClient;
  });
  const docDe = (x) => {
    // « Chantier Martin » plutôt que « Chantier Chantier Martin ».
    if (x.genre === 'CHANTIER')
      return /^chantier\b/i.test(x.chantier || '')
        ? x.chantier
        : trf('Chantier {0}', x.chantier || '');
    const doc =
      x.genre === 'DEVIS'
        ? (devis || []).find((d) => d.id === x.documentId)
        : (factures || []).find((f) => f.id === x.documentId);
    const num = (doc && numeroDe(doc)) || x.documentId;
    return x.genre === 'DEVIS' ? trf('Offre {0}', num) : trf('Facture {0}', num);
  };
  // Les achats du client, chacun une fois (piecesDesDepenses) : la facture
  // jointe à l'offre et saisie au chantier, le sous-traitant à son prix
  // convenu et à sa facture, s'additionnaient — 22'224.90 d'achats annoncés
  // pour 12'412.65 réels.
  const ouEst = (g) =>
    g.source === 'joint' ? docDe(g.joint) : docDe({ genre: 'CHANTIER', chantier: g.chantier?.nom });
  const achats = piecesDesDepenses({
    chantiers: chantiersDuClient,
    achatsJoints: surLesDocuments.filter((x) => estUnAchat(x.piece)),
    factures,
  }).map((g) => {
    const aussi = g.doublons.map(ouEst).filter(Boolean);
    if (g.source === 'joint') return { ...g.joint, aussi };
    const x = g.item;
    if (g.source === 'soustraitant') {
      return {
        genre: 'CHANTIER',
        chantier: g.chantier.nom,
        aussi,
        piece: {
          id: 'ch-st-' + x.id,
          pieceId: x.pieceId,
          fournisseur: x.entreprise,
          libelle: g.dejaFacture > 0 ? tr('Reste du prix convenu') : x.metier || x.entreprise,
          categorie: 'Sous-traitance',
          montant: g.montant,
        },
      };
    }
    return {
      genre: 'CHANTIER',
      chantier: g.chantier.nom,
      aussi,
      piece: {
        id: 'ch-f-' + x.id,
        pieceId: x.pieceId,
        fournisseur: x.fournisseur,
        libelle: x.fournisseur,
        reference: x.ref,
        categorie: x.cat,
        montant: x.montant,
        date: x.date,
      },
    };
  });
  const photos = sansPhotos
    ? []
    : surLesDocuments.filter((x) => !estUnAchat(x.piece) && estImage(x.piece));
  const fichiers = sansPhotos
    ? []
    : surLesDocuments.filter((x) => !estUnAchat(x.piece) && !estImage(x.piece) && x.piece.fichier);
  if (!achats.length && !photos.length && !fichiers.length && !photosChantier.length) return null;
  const total = achats.reduce((s, x) => s + (+x.piece.montant || 0), 0);
  const groupes = [];
  achats.forEach((x) => {
    const doc = docDe(x);
    const g = groupes.find(([cle]) => cle === doc);
    if (g) g[1].push(x);
    else groupes.push([doc, [x]]);
  });
  const titre = { fontSize: 9.5, fontWeight: 800, color: C.grey, margin: '8px 0 6px' };
  return enveloppe(
    <div data-testid="liste-du-client">
      {achats.length > 0 && (
        <>
          <div style={titre}>{tr('🧾 MARCHANDISE ET SOUS-TRAITANCE')}</div>
          {/* Les achats rangés sous leur facture (ou offre, ou chantier), avec
              leur total : mêlés dans une seule liste, on ne savait plus quel
              matériel allait avec quelle facture. */}
          {groupes.map(([doc, lot]) => (
            <div key={doc} style={{ marginBottom: 8 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  background: C.navyXL,
                  borderRadius: 8,
                  padding: '6px 9px',
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: C.ink,
                }}
              >
                <span>{doc}</span>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {DEVISE} {fmt(lot.reduce((t, x) => t + (+x.piece.montant || 0), 0))}
                </span>
              </div>
              {lot.map((x) => (
                <div
                  key={x.piece.id}
                  role={x.piece.fichier || x.piece.pieceId ? 'button' : undefined}
                  onClick={
                    x.piece.fichier
                      ? () => ouvrePiece(x.piece)
                      : x.piece.pieceId
                        ? () => ouvrePiece({ id: x.piece.pieceId, libelle: x.piece.fournisseur })
                        : undefined
                  }
                  style={{
                    display: 'flex',
                    gap: 9,
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid ' + C.border,
                    cursor: x.piece.fichier || x.piece.pieceId ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ fontSize: 18 }}>{deSousTraitance(x.piece) ? '🤝' : '🧾'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 800 }}>
                      {x.piece.fournisseur || x.piece.libelle || tr('Facture fournisseur')}
                    </div>
                    <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}>
                      {[
                        x.piece.fournisseur && x.piece.libelle !== x.piece.fournisseur
                          ? x.piece.libelle
                          : '',
                        x.piece.categorie,
                        x.piece.date,
                        x.aussi?.length ? trf('aussi : {0}', x.aussi.join(', ')) : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {x.piece.montant != null && x.piece.montant !== '' && (
                    <b style={{ fontSize: 11, color: C.purple, whiteSpace: 'nowrap' }}>
                      {DEVISE} {fmt(+x.piece.montant)}
                    </b>
                  )}
                </div>
              ))}
            </div>
          ))}
          {total > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                fontWeight: 800,
                padding: '8px 0 2px',
              }}
            >
              <span>{tr('Total des achats')}</span>
              <span>
                {DEVISE} {fmt(total)}
              </span>
            </div>
          )}
        </>
      )}
      {photos.length > 0 && (
        <>
          <div style={titre}>{tr('📷 PHOTOS')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
            {photos.map((x) => (
              <div
                key={x.piece.id}
                role="button"
                onClick={() => ouvrePiece(x.piece)}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={adresseApi(x.piece.fichier)}
                  alt={x.piece.libelle || ''}
                  loading="lazy"
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    objectFit: 'cover',
                    borderRadius: 8,
                    display: 'block',
                  }}
                />
                <div style={{ fontSize: 8, color: C.muted, marginTop: 2 }}>
                  {[
                    x.piece.libelle === 'Photo avant'
                      ? tr('Avant')
                      : x.piece.libelle === 'Photo après'
                        ? tr('Après')
                        : '',
                    docDe(x),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {photosChantier.length > 0 && (
        <>
          <div style={titre}>{tr('🏗️ PHOTOS DU CHANTIER')}</div>
          {ETAPES.map(([cat, libelle]) => {
            const lot = photosChantier.filter(
              (ph) => (ETAPES_CONNUES.has(ph.cat) ? ph.cat : 'pendant') === cat,
            );
            if (!lot.length) return null;
            return (
              <div key={cat} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.muted, margin: '4px 0' }}>
                  {tr(libelle)} · {lot.length}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
                  {lot.map((ph) => (
                    <div
                      key={ph.id}
                      role="button"
                      onClick={() => {
                        if (!ouvreVisionneuse({ adresse: ph.src, titre: ph.label || ph.chantier }))
                          window.open(ph.src, '_blank');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <img
                        src={ph.src}
                        alt={ph.label || ''}
                        loading="lazy"
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          objectFit: 'cover',
                          borderRadius: 8,
                          display: 'block',
                        }}
                      />
                      <div style={{ fontSize: 8, color: C.muted, marginTop: 2 }}>
                        {[ph.date, ph.chantier].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
      {fichiers.length > 0 && (
        <>
          <div style={titre}>{tr('📎 AUTRES DOCUMENTS')}</div>
          {fichiers.map((x) => (
            <div
              key={x.piece.id}
              role="button"
              onClick={() => ouvrePiece(x.piece)}
              style={{
                fontSize: 10,
                padding: '7px 0',
                borderBottom: '1px solid ' + C.border,
                cursor: 'pointer',
              }}
            >
              📄 {x.piece.libelle || tr('Document')} · {docDe(x)}
            </div>
          ))}
        </>
      )}
    </div>,
  );
}
